<template>
  <v-dialog
    scrollable
    width="950px"
    :value="show"
    @click:outside="$emit('close')"
    @keydown.esc="$emit('close')"
  >
    <v-card height="755">
      <v-card-title class="px-6">
        {{ $t('routines.advanced_selector.advanced_selection_title') }}
      </v-card-title>
      <v-divider></v-divider>

      <v-card-text class="py-0 my-0">
        <div class="d-flex justify-end align-center mt-5">
          <span
            v-if="showClearAllButton"
            class="blue--text cursor-pointer mr-4"
            @click="onClearAll"
          >
            {{ $t('routines.advanced_selector.clear_all_filters') }}</span
          >

          <SearchInput autofocus :value="searchTerms" @change="handleSearch" />
        </div>

        <div v-if="!showSelectedRoutines" class="filters-container mt-4">
          <Filters
            hideDeletableWrapper
            disableApplyButton
            hideSaveButton
            hideApplyButton
            hideAddButton
            enableAdditionalFilters
            :fixedWidth="271"
            :initialFilters="filters"
            @change="handleFilterChange"
          />
        </div>

        <div>
          <v-sheet
            rounded
            outlined
            class="sheet-container"
            :class="showSelectedRoutines ? 'mt-4' : 'mt-0'"
          >
            <v-data-table
              class="advanced-routine-selector-table"
              v-if="!showSelectedRoutines"
              v-model="selectedRoutines"
              show-select
              :hide-default-footer="hideFooter"
              :items-per-page="itemsPerPage"
              :loading="isLoading"
              :headers="headers"
              :items="routines"
              :options.sync="options"
              :server-items-length="totalItems"
              :footer-props="footerOptions"
            >
              <template v-slot:item.data-table-select="{ isSelected, select }">
                <v-checkbox
                  class="ma-0 pa-0"
                  color="blue"
                  hide-details
                  :ripple="false"
                  :input-value="isSelected"
                  @change="select($event)"
                />
              </template>

              <template v-slot:item.name="{ item }">
                <div class="d-flex justify-start align-center">
                  <text-highlight
                    highlightClass="yellow lighten-1"
                    data-private
                    highlightComponent="span"
                    :queries="getHighlight(item)"
                    :title="item.name"
                  >
                    {{ item.name }}
                  </text-highlight>
                </div>
              </template>

              <template v-slot:item.typeName="{ item }">
                <span>
                  {{ item.typeName }}
                </span>
              </template>

              <template v-slot:item.situationType="{ item }">
                <span>
                  {{
                    item.situationType
                      ? $t(
                          `quick_action.situation_types.situations.${item.situationType}`
                        )
                      : ''
                  }}
                </span>
              </template>

              <template v-slot:item.tags="{ item }">
                <div class="pt-2">
                  <ChipList
                    outlined
                    :totalItems="item.tags ? item.tags.length : 0"
                    :items="getTags(item.tags)"
                    :limit="2"
                  />
                </div>
              </template>

              <template v-slot:item.createdAt="{ item }">
                <span class="subtitle blue-grey--text text--darken-3">
                  {{ item.createdAt }}
                </span>
              </template>

              <template v-slot:no-data>
                <div
                  v-if="!isLoading"
                  class="d-flex justify-center align-center no-data-container"
                  style="height:350px"
                >
                  <EmptyState
                    icon="mdi-card-search-outline"
                    :iconSize="36"
                    :title="$t('routines.advanced_selector.empty_state_title')"
                    :message="
                      $t('routines.advanced_selector.empty_state_message')
                    "
                  />
                </div>
              </template>
            </v-data-table>

            <v-data-table
              class="advanced-routine-selector-table"
              v-if="showSelectedRoutines"
              v-model="selectedRoutines"
              show-select
              height="424"
              :hide-default-footer="hideFooter"
              :loading="isLoading"
              :headers="headers"
              :items="selectedRoutines"
              :search="searchTerms"
              :items-length="selectedRoutines.length"
              :items-per-page="-1"
              :selected.sync="selectedRoutines"
            >
              <template v-slot:item.data-table-select="{ isSelected, select }">
                <v-checkbox
                  class="ma-0 pa-0"
                  color="blue"
                  hide-details
                  :ripple="false"
                  :input-value="isSelected"
                  @change="select($event)"
                />
              </template>
              <template v-slot:item.name="{ item }">
                <text-highlight
                  highlightClass="yellow lighten-1"
                  data-private
                  highlightComponent="span"
                  :queries="searchTerms"
                  :title="item.name"
                >
                  {{ item.name }}
                </text-highlight>
              </template>

              <template v-slot:item.typeName="{ item }">
                <span>
                  {{ item.typeName }}
                </span>
              </template>

              <template v-slot:item.situationType="{ item }">
                <span class="situation-label">
                  {{
                    item.situationType
                      ? $t(
                          `quick_action.situation_types.situations.${item.situationType}`
                        )
                      : ''
                  }}
                </span>
              </template>

              <template v-slot:item.tags="{ item }">
                <ChipList
                  outlined
                  :totalItems="item.tags ? item.tags.length : 0"
                  :items="getTags(item.tags)"
                  :limit="2"
                />
              </template>

              <template v-slot:item.createdAt="{ item }">
                <span class="subtitle blue-grey--text text--darken-3">
                  {{ item.createdAt }}
                </span>
              </template>
              <template v-slot:no-data>
                <div
                  class="d-flex justify-center align-center no-data-container"
                  style="height:300px"
                >
                  <EmptyState
                    icon="mdi-card-search-outline"
                    :iconSize="36"
                    :title="$t('routines.advanced_selector.empty_state_title')"
                    :message="
                      $t('routines.advanced_selector.empty_state_message')
                    "
                  />
                </div>
              </template>
            </v-data-table>
            <span
              v-if="!hideFooter"
              class="footer-info caption blue-grey--text text--darken-1"
            >
              {{
                !showSelectedRoutines
                  ? $t('common.resultWithCount_other', {
                      count: totalItems,
                    })
                  : `${selectedRoutines.length} ${$t('common.selected')}`
              }}</span
            >
          </v-sheet>
        </div>
      </v-card-text>

      <v-card-actions
        class="px-6 py-4 align-center"
        :class="hasSelectedRoutines ? 'justify-space-between' : 'justify-end'"
      >
        <Button
          v-if="hasSelectedRoutines"
          plain
          :text="
            !showSelectedRoutines
              ? `${$t('routines.advanced_selector.view_all_selected')} (${
                  selectedRoutines.length
                })`
              : $t('common.go_back')
          "
          :icon="showSelectedRoutines ? 'mdi-chevron-left' : ''"
          @click="handleShowSelectedRoutines"
        />

        <div class="justify-end pb-0 mb-0">
          <Button
            plain
            color="grey"
            :text="$t('common.button.cancel')"
            @click="$emit('close')"
            class="mr-2"
          />
          <Button
            :disabled="!hasSelectedRoutines"
            :text="
              hasSelectedRoutines
                ? `${$t('common.button.select')} ${selectedRoutines.length}`
                : $t('common.button.select')
            "
            @click="$emit('selectRoutines', selectedRoutines)"
          />
        </div>
      </v-card-actions>
    </v-card>
  </v-dialog>
</template>

<script>
import _ from 'lodash'

import { mapGetters } from 'vuex'
import api from '@/api'
import mixin from '@/mixins/mixin.vue'

import Button from '@/components/UI/Button'
import SearchInput from '@/components/Core/SearchInput.vue'
import EmptyState from '@/components/EmptyState.vue'
import ChipList from '@/components/ChipList.vue'
import { utils } from 'frontend-js-shared'

const IS_EXECUTABLE_BY_ME = 'executableByMe'
const CREATED_BY_ME = 'createdByMe'
const COLLABORATOR = 'collaborator'

export default {
  name: 'AdvancedRoutineSelector',

  components: {
    Button,
    SearchInput,
    EmptyState,
    ChipList,
    // This import prevents recursive error. In this case: Filters.vue imports RoutineSelector.vue. RoutineSelector.vue imports AdvancedRoutineSelector.vue which imports Filters.vue. This loop
    // causes the Filters.vue component not to be loaded correctly and a console error is thrown.
    // This is not the best solution, but it works for now. A better solution would be to refactor the code to avoid this circular dependency.
    Filters: () => import('@/components/Filters/Filters.vue'),
  },

  mixins: [mixin],

  props: {
    show: {
      type: Boolean,
      required: true,
      default: false,
    },
    routineDefinitionPresetFilter: {
      type: String,
      required: false,
      default: 'allRoutines',
    },
    selectedRoutineIds: {
      type: Array,
      default: () => [],
    },

    selectedRoutinesData: {
      type: Array,
      default: () => [],
    },
  },
  data() {
    return {
      searchTerms: '',
      isLoading: true,
      showSelectedRoutines: false,
      filters: {
        routineDefinitionsPresetFilter: '',
        routineType: [],
        createdAt: {},
        createdAtOption: '',
        categories: [],
        tags: [],
        situationTypes: [],
      },
      selectedRoutines: [],
      routines: [],
      currentPage: 1,
      itemsPerPage: 20,
      totalItems: 0,
      sortBy: 'name',
      sortDesc: false,
      showClearAllButton: false,
      headers: [
        {
          text: this.$t('common.name'),
          value: 'name',
          sortable: true,
          width: '200px',
        },
        {
          text: this.$t('common.routine_type'),
          sortable: false,
          value: 'typeName',
        },
        {
          text: this.$t('organization_settings.situation_type'),
          sortable: false,
          value: 'situationType',
        },
        {
          text: this.$t('common.tag_other'),
          sortable: false,
          value: 'tags',
        },
        {
          text: this.$t('filters.date.creation_date'),
          value: 'createdAt',
          show: false,
        },
      ],
      footerOptions: {
        itemsPerPageOptions: [20, 50, 100],
      },
    }
  },
  watch: {
    searchTerms: {
      handler: _.debounce(function(value) {
        if (!this.showSelectedRoutines || value) {
          this.fetchRoutineDefinitions()
        }
      }, 700),
    },
    filters: {
      handler() {
        this.fetchRoutineDefinitions()
        const {
          situationTypes,
          tags,
          categories,
          createdAt,
          routineType,
        } = this.filters

        this.showClearAllButton =
          situationTypes.length != 0 ||
          tags.length != 0 ||
          categories.length != 0 ||
          !_.isEmpty(createdAt) ||
          routineType.length != 0
      },
      deep: true,
    },

    hasSelectedRoutines(totalSelectedRoutines) {
      if (!totalSelectedRoutines) {
        this.showSelectedRoutines = false
      }
    },
  },

  mounted() {
    this.filters = { ...this.defaultFilters }
    this.selectedRoutines = [...this.selectedRoutinesData]
    this.fetchRoutineDefinitions()
  },

  computed: {
    ...mapGetters(['organizationId', 'accountId']),

    defaultHighlightQueries() {
      return [this.searchTerm ?? '', ...(this.searchTerm?.split?.(' ') ?? [])]
    },

    options: {
      get() {
        return {
          page: this.currentPage,
          itemsPerPage: this.itemsPerPage,
          sortBy: this.sortBy ? [this.sortBy] : [],
          sortDesc: this.sortDesc !== null ? [this.sortDesc] : [],
        }
      },
      set(event) {
        const pageChange = this.currentPage !== event.page
        const itemsPerPageChange = this.itemsPerPage !== event.itemsPerPage
        const sortBy = event.sortBy.length ? event.sortBy[0] : null
        const sortDesc = event.sortDesc.length ? event.sortDesc[0] : null
        const sortByChange = this.sortBy !== sortBy
        const sortDescChange = this.sortDesc !== sortDesc

        if (
          pageChange ||
          itemsPerPageChange ||
          sortByChange ||
          sortDescChange
        ) {
          this.currentPage = event.page
          this.itemsPerPage = event.itemsPerPage
          this.sortBy = sortBy
          this.sortDesc = sortDesc

          this.fetchRoutineDefinitions()
        }
      },
    },

    defaultFilters() {
      return {
        routineDefinitionsPresetFilter: this.routineDefinitionPresetFilter,
        routineType: [],
        createdAt: [],
        categories: [],
        tags: [],
        situationTypes: [],
      }
    },

    hasSelectedRoutines() {
      return this.selectedRoutines.length > 0
    },

    hasRoutines() {
      return this.routines.length > 0
    },

    hideFooter() {
      return !this.hasRoutines && !this.isLoading
    },

    routineTypes() {
      return this.$constants.routines.types.map(type => ({
        value: type.value,
        text: this.$t(type?.name),
      }))
    },
  },

  methods: {
    handleShowSelectedRoutines() {
      this.searchTerms = ''
      this.showSelectedRoutines = !this.showSelectedRoutines
    },
    async fetchRoutineDefinitions() {
      if (this.showSelectedRoutines) return
      try {
        this.isLoading = true
        const from = (this.currentPage - 1) * this.itemsPerPage

        const params = {
          filters: {
            createdAt: this.filters.createdAt,
            categories: this.filters.categories,
            tags: this.filters.tags,
            situations: this.filters.situationTypes,
            excludedRoutines: this.selectedRoutineIds,
            types: this.filters.routineType,
          },
          from: from,
          limit: this.itemsPerPage,
          terms: this.searchTerms,
          sort: {
            by: this.searchTerms ? '_score' : this.sortBy || 'name',
            order: this.searchTerms ? 'desc' : this.sortDesc ? 'desc' : 'asc',
          },
          organizationId: this.organizationId,
          expansions: ['presetTags'],
          projections: [
            'name',
            'organization',
            'updatedAt',
            'presetTags',
            'createdAt',
            'type',
            'presetSituationTypes',
          ],
        }

        if (
          this.filters.routineDefinitionsPresetFilter === IS_EXECUTABLE_BY_ME
        ) {
          params.filters.isExecutableByMe = true
        }
        if (this.filters.routineDefinitionsPresetFilter === CREATED_BY_ME) {
          params.filters.createdBy = this.accountId
        }
        if (this.filters.routineDefinitionsPresetFilter === COLLABORATOR) {
          params.filters.collaborators = [this.accountId]
        }

        const response = await api.routine.getStreamRoutineDefinition(params)

        this.routines = response.data.results.map(routineDefinition => ({
          name: routineDefinition?.result?.name,
          id: routineDefinition?.result?.id,
          tags: routineDefinition?.result?.presetTags,
          type: routineDefinition?.result?.type,
          situationType:
            routineDefinition?.result?.presetSituationTypes[0] ?? '',
          typeName:
            this.routineTypes?.find?.(
              rt => rt.value === routineDefinition?.result?.type
            )?.text ?? '',
          highlights: routineDefinition?.highlights || {},
          createdAt: routineDefinition?.result?.createdAt
            ? utils.dates.formatDate(
                routineDefinition?.result?.createdAt,
                'MMM D, YYYY'
              )
            : '-',
        }))
        this.totalItems = response.data.totalRecordsInData
      } catch (error) {
        this.showError(error)
      } finally {
        this.isLoading = false
      }
    },

    handleSearch(searchTerm) {
      this.searchTerms = searchTerm
      this.currentPage = 1
    },
    onClearAll() {
      this.filters = {
        ...this.defaultFilters,
      }
      this.searchTerms = ''
    },

    handleFilterChange({ serializedFilters }) {
      this.filters.routineDefinitionsPresetFilter =
        serializedFilters.routineDefinitionsPresetFilter
      this.filters.categories = serializedFilters.categories
      this.filters.tags = serializedFilters.tags
      this.filters.situationTypes = serializedFilters.situationTypes
      this.filters.createdAt = serializedFilters.createdAt
      this.filters.createdAtOption = serializedFilters.createdAtOption
      this.filters.routineType = serializedFilters.type
    },

    getTags(tags = []) {
      return tags.map(tag => tag?.name)
    },

    getHighlight(item) {
      return item?.highlights?.name || this.defaultHighlightQueries
    },
  },
}
</script>
<style lang="scss" scoped>
.footer-info {
  position: absolute;
  bottom: 18px;
  left: 20px;
}

.sheet-container {
  position: relative;
}
</style>
<style lang="scss">
.advanced-routine-selector-table {
  th {
    span {
      color: $color-blue-grey-darken1 !important;
    }
  }
  tr:hover {
    background-color: $color-blue-grey-lighten3 !important;
  }
  .v-data-table-header {
    white-space: nowrap;
  }
}
</style>
